import React, { useEffect, useMemo, useRef, useState, lazy } from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { RouterProvider } from 'react-router-dom';
import { router } from './router';
import { ThemeMount } from './theme/ThemeMount';
import { ToastHost } from './components/ToastHost';
import { ConfirmDialogHost } from './components/ConfirmDialog';
import {
  cacheKeyFor,
  makePersister,
  shouldDehydrateQuery,
  wipeAllPersistedCaches,
} from './offline/persist';
import '@slaplink/ui-tokens/tokens.css';
import './styles/app.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 1000 * 60 * 60 * 24 * 14, // keep unused queries around for 2 weeks
      refetchOnWindowFocus: false,
      // Don't refetch on reconnect too aggressively; rely on mutations + normal
      // staleTime. Keeps "offline reads" feel snappy without a thundering herd
      // when Wi-Fi flaps.
      refetchOnReconnect: 'always',
      retry: 1,
      networkMode: 'offlineFirst',
    },
    mutations: {
      // Mutations still require the network. When offline they fail fast so the
      // UI can surface an error (we don't yet queue writes - deliberate).
      networkMode: 'online',
      retry: 0,
    },
  },
});

import { usePublicConfig, type PublicConfig } from './api/public-config';

const DEFAULT_PUBLIC_CONFIG: PublicConfig = {
  clientCacheMaxDays: 14,
  pageCacheEnabled: true,
  sessionShortTtlHours: 8,
  sessionLongTtlDays: 30,
  tiersVisible: false,
  paymentsEnabled: false,
  adsGloballyEnabled: false,
  adsBlockerPolicy: 'STATS_ONLY',
  soundsEnabledByDefault: true,
  soundShareTone: 'ascending',
  soundNotificationTone: 'three-tone',
};

function AppRoot(): React.ReactElement | null {
  const [userId, setUserId] = useState<string | null>(null);
  const [publicCfg, setPublicCfg] = useState<PublicConfig>(DEFAULT_PUBLIC_CONFIG);
  const [ready, setReady] = useState(false);
  const [adBlocked, setAdBlocked] = useState(false);
  const lastUserId = useRef<string | null | undefined>(undefined);

  // Resolve the current session once before deciding which user-scoped
  // persister to use. We intentionally don't put /auth/session in the
  // persisted cache, so a tiny network probe here is the source of truth.
  // When offline, the fetch fails and we fall back to the last-known userId
  // from localStorage so the correct cache rehydrates.
  useEffect(() => {
    let cancelled = false;
    const LAST_UID_KEY = 'sl.last-user-id';
    const lastKnown = (() => {
      try { return localStorage.getItem(LAST_UID_KEY); } catch { return null; }
    })();
    Promise.all([
      fetch('/api/auth/session', { credentials: 'include' })
        .then((r) => (r.ok ? r.json() : { user: null }))
        .then((d: { user: { id: string } | null }) => ({ online: true, id: d.user?.id ?? null }))
        .catch(() => ({ online: false, id: lastKnown })),
      fetch('/api/public-config', { credentials: 'include' })
        .then((r) => (r.ok ? r.json() : DEFAULT_PUBLIC_CONFIG))
        .catch(() => DEFAULT_PUBLIC_CONFIG),
    ])
      .then(async ([sess, pub]: [{ online: boolean; id: string | null }, PublicConfig]) => {
        setPublicCfg(pub);
        // Mirror into the global store so tier-error helper can read it
        // without prop-drilling through every component that needs it.
        usePublicConfig.getState().setConfig(pub);
        return sess;
      })
      .then(async (res: { online: boolean; id: string | null }) => {
        if (cancelled) return;
        const next = res.id;
        // User switched (or logged out) between page loads? Wipe everything so
        // user A can never see user B's cached groups/sets/links.
        if (res.online && lastUserId.current !== undefined && lastUserId.current !== next) {
          await wipeAllPersistedCaches();
          queryClient.clear();
        }
        lastUserId.current = next;
        setUserId(next);
        setReady(true);
        if (res.online) {
          try {
            if (next) localStorage.setItem(LAST_UID_KEY, next);
            else localStorage.removeItem(LAST_UID_KEY);
          } catch { /* private mode or quota */ }
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Run ad-blocker detection once the user is known and ads are globally
  // enabled. Result drives the SOFT_NUDGE banner and HARD_GATE overlay
  // mounted under the router. STATS_ONLY mode just reports silently.
  useEffect(() => {
    if (!userId || !publicCfg.adsGloballyEnabled) return;
    let cancelled = false;
    void (async () => {
      const { detectAdBlocker, reportAdBlockerDetected } = await import('./lib/ads-detection');
      const blocked = await detectAdBlocker();
      if (cancelled) return;
      if (blocked) {
        setAdBlocked(true);
        void reportAdBlockerDetected();
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, publicCfg.adsGloballyEnabled]);

  const { persister, cacheKey } = useMemo(() => makePersister(userId), [userId]);

  if (!ready) {
    return <div className="sl-loading">Loading…</div>;
  }

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        maxAge: 1000 * 60 * 60 * 24 * Math.max(1, publicCfg.clientCacheMaxDays),
        buster: `${cacheKey}:${publicCfg.clientCacheMaxDays}`, // bump resets the cache
        dehydrateOptions: { shouldDehydrateQuery },
      }}
    >
      <ThemeMount />
      {adBlocked && publicCfg.adsBlockerPolicy === 'SOFT_NUDGE' && <AdBlockerSoftNudgeMount />}
      <RouterProvider router={router} />
      <ToastHost />
      <ConfirmDialogHost />
      {adBlocked && publicCfg.adsBlockerPolicy === 'HARD_GATE' && <AdBlockerHardGateMount />}
    </PersistQueryClientProvider>
  );
}

const AdBlockerSoftNudgeMount = lazy(() =>
  import('./components/AdBlockerNudge').then((m) => ({ default: m.AdBlockerSoftNudge })),
);
const AdBlockerHardGateMount = lazy(() =>
  import('./components/AdBlockerNudge').then((m) => ({ default: m.AdBlockerHardGate })),
);

const root = document.getElementById('root');
if (!root) throw new Error('missing #root');

ReactDOM.createRoot(root).render(
  <React.StrictMode>
    <AppRoot />
  </React.StrictMode>,
);
